/**
 * Saidiseire's settings: the engine switch, how often to check, how many sites
 * at once, who is told, and the outside heartbeat.
 *
 * Read fresh when the dialog opens (the list only carries a summary of these)
 * and saved as one PATCH holding only what changed, so two people with the
 * dialog open do not overwrite each other's untouched fields.
 *
 * ## Recipients: nobody picked means everybody
 *
 * An empty list is not "tell nobody" - it is the whole CRM desk, which is the
 * safe default for an outage alarm. The field says so on its face ("everyone
 * with CRM access") rather than showing a blank that reads as switched off.
 * The candidates are the server's list; an id it no longer offers is dropped
 * from the field rather than sent back and refused.
 *
 * ## The heartbeat address is write-only
 *
 * It is a secret: anybody holding it can keep the outside alarm quiet. The
 * server never returns it, so the field starts empty and says whether one is
 * set. Typing saves a new one; the remove button clears it. Leaving the field
 * empty changes nothing.
 */
import { useEffect, useId, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Label, Switch } from "@wa/ui";
import {
  HEARTBEAT_URL_MAX,
  MONITOR_LIMITS,
  MONITOR_RECIPIENTS_MAX,
  clampSetting,
  getMonitorSettings,
  heartbeatProblem,
  monitorErrorKey,
  saveMonitorSettings,
  type MonitorNumberSetting,
  type MonitorSettingsPatch,
  type MonitorSettingsView,
} from "../../../lib/monitoring";
import { Btn, Modal, MultiMenuField, useToast } from "../../ui";
import { Field, Pill } from "../_shared/formBits";
import type { T } from "./words";

function NumberInput({
  field,
  value,
  onChange,
  t,
}: {
  field: MonitorNumberSetting;
  value: string;
  onChange: (v: string) => void;
  t: T;
}) {
  const { min, max } = MONITOR_LIMITS[field];
  // The interval is a default now: a site on a web maintenance package is checked at its package's pace.
  const hint = field === "intervalMinutes" ? t("monitoring.settings.intervalHint", { min, max }) : t("monitoring.settings.range", { min, max });
  return (
    <div className="min-w-0">
      <Field label={t(`monitoring.settings.${field}`)} hint={hint}>
        <Pill value={value} onChange={onChange} inputMode="numeric" />
      </Field>
    </div>
  );
}

export function SettingsDialog({
  t,
  onClose,
  onSaved,
}: {
  t: T;
  onClose: () => void;
  onSaved: (settings: MonitorSettingsView) => void;
}) {
  const toast = useToast();
  const enabledId = useId();
  const [stored, setStored] = useState<MonitorSettingsView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [interval, setIntervalText] = useState("");
  const [concurrency, setConcurrency] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [heartbeat, setHeartbeat] = useState("");
  const [clearHeartbeat, setClearHeartbeat] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getMonitorSettings()
      .then((s) => {
        if (!alive) return;
        setStored(s);
        setEnabled(s.enabled);
        setIntervalText(String(s.intervalMinutes));
        setConcurrency(String(s.concurrency));
        const offered = new Set(s.candidates.map((c) => c.userId));
        setRecipients(s.recipientUserIds.filter((id) => offered.has(id)));
      })
      .catch(() => {
        if (alive) setLoadFailed(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const heading = t("monitoring.settings.title");
  const hbProblem = heartbeatProblem(heartbeat);

  async function save() {
    if (!stored || busy || hbProblem) return;
    const patch: MonitorSettingsPatch = {};
    if (enabled !== stored.enabled) patch.enabled = enabled;
    const minutes = clampSetting("intervalMinutes", interval, stored.intervalMinutes);
    if (minutes !== stored.intervalMinutes) patch.intervalMinutes = minutes;
    const workers = clampSetting("concurrency", concurrency, stored.concurrency);
    if (workers !== stored.concurrency) patch.concurrency = workers;
    const same =
      recipients.length === stored.recipientUserIds.length &&
      recipients.every((id) => stored.recipientUserIds.includes(id));
    if (!same) patch.recipientUserIds = recipients;
    if (heartbeat.trim()) patch.heartbeatUrl = heartbeat.trim();
    else if (clearHeartbeat) patch.heartbeatUrl = null;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await saveMonitorSettings(patch);
      onSaved(next);
      toast(t("monitoring.toast.settingsSaved"));
      onClose();
    } catch (err) {
      setError(t(`monitoring.error.${monitorErrorKey(err)}`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={heading} label={heading} onClose={onClose} className="max-w-[560px]">
      {!stored ? (
        <div className="flex justify-center py-10 text-muted-foreground">
          {loadFailed ? (
            <p className="text-[13px] text-[var(--color-destructive)]">{t("monitoring.settings.loadFailed")}</p>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      ) : (
        <>
          <div className="crm-inp mt-1 gap-3">
            <Switch id={enabledId} checked={enabled} onCheckedChange={setEnabled} />
            <Label htmlFor={enabledId} className="min-w-0 flex-1 text-[13.5px] font-normal">
              {t("monitoring.settings.enabled")}
            </Label>
          </div>
          <p className="crm-fnote mt-1">{t("monitoring.settings.enabledHint")}</p>

          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <NumberInput field="intervalMinutes" value={interval} onChange={setIntervalText} t={t} />
            <NumberInput field="concurrency" value={concurrency} onChange={setConcurrency} t={t} />
          </div>

          <Field label={t("monitoring.settings.recipients")} hint={t("monitoring.settings.recipientsHint", { max: MONITOR_RECIPIENTS_MAX })}>
            <MultiMenuField
              variant="form"
              value={recipients}
              // The route refuses more than the limit; a tick past it is ignored
              // here rather than failing the whole save afterwards.
              onChange={(next) => setRecipients(next.slice(0, MONITOR_RECIPIENTS_MAX))}
              label={t("monitoring.settings.recipients")}
              allLabel={t("monitoring.settings.everyone")}
              countLabel={(n) => t("common.nSelected", { n })}
              clearLabel={t("common.clear")}
              options={stored.candidates.map((c) => ({ value: c.userId, label: c.name ?? c.email }))}
            />
          </Field>

          <Field
            label={t("monitoring.settings.heartbeat")}
            hint={
              hbProblem ? (
                <span className="text-[var(--color-destructive)]">
                  {t(`monitoring.settings.heartbeatProblem.${hbProblem}`, { max: HEARTBEAT_URL_MAX })}
                </span>
              ) : stored.heartbeatConfigured && !clearHeartbeat ? (
                t("monitoring.settings.heartbeatSet")
              ) : (
                t("monitoring.settings.heartbeatHint")
              )
            }
          >
            <div className="flex items-center gap-2">
              <label className="crm-inp min-w-0 flex-1">
                <input
                  value={heartbeat}
                  onChange={(e) => {
                    setHeartbeat(e.target.value);
                    if (e.target.value) setClearHeartbeat(false);
                  }}
                  placeholder={
                    stored.heartbeatConfigured && !clearHeartbeat
                      ? t("monitoring.settings.heartbeatKeep")
                      : "https://hc-ping.com/..."
                  }
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  data-1p-ignore=""
                  data-lpignore="true"
                />
              </label>
              {stored.heartbeatConfigured && !clearHeartbeat ? (
                <Btn
                  small
                  tone="plain"
                  onClick={() => {
                    setClearHeartbeat(true);
                    setHeartbeat("");
                  }}
                  title={t("monitoring.settings.heartbeatRemove")}
                  aria-label={t("monitoring.settings.heartbeatRemove")}
                >
                  <Trash2 className="size-[15px]" strokeWidth={1.8} />
                </Btn>
              ) : null}
            </div>
          </Field>

          {error ? (
            <p className="mt-3 text-[13px] text-[var(--color-destructive)]" role="alert">
              {error}
            </p>
          ) : null}
        </>
      )}

      <div className="crm-m-foot">
        <Btn type="button" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </Btn>
        <Btn type="button" tone="dark" disabled={busy || !stored || Boolean(hbProblem)} onClick={() => void save()}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {t("common.save")}
        </Btn>
      </div>
    </Modal>
  );
}
